import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'

type NewPasswordParams = {
  token: string
  password: string
}

async function newPassword({ token, password }: NewPasswordParams) {
  return api.backend.post(
    '/user/reset-password',
    { password },
    { headers: { 'verify-token': token } },
  )
}

export function useNewPassword() {
  const { t } = useTranslation('login')
  const { mutateAsync, isPending, isSuccess, isError } = useMutation<
    unknown,
    AxiosError<ApiError>,
    NewPasswordParams
  >({
    mutationKey: ['/user/reset-password'],
    mutationFn: newPassword,
    onSuccess: () => {
      toast(Toast, {
        toastId: 'new-password-success',
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t('forgot_password.new_password.toast.success.title'),
          description: t('forgot_password.new_password.toast.success.message'),
        },
      })
    },
    onError: (error) => {
      toast(Toast, {
        toastId: 'new-password-error',
        data: {
          type: 'danger',
          icon: TriangleAlert,
          title: t('forgot_password.new_password.toast.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('forgot_password.new_password.toast.error.message'),
        },
      })
    },
  })

  const handleNewPassword = async (token: string, password: string) => {
    await mutateAsync({ token, password })
  }

  return {
    handleNewPassword,
    isPendingNewPassword: isPending,
    isSuccessNewPassword: isSuccess,
    isErrorNewPassword: isError,
  }
}
